import { useEffect, useRef, useState, type ReactNode } from 'react';
import { content } from '../content';
import {
  createInitialState,
  dispatch,
  GameRuleError,
  resolveCardDefinition,
} from '../core/engine';
import type { GameCommand, GameEvent, GameState } from '../core/types';
import { exposeBrowserControl } from '../devtools/browserControl';
import { GameAudio } from '../audio/gameAudio';
import {
  clearSave,
  loadGame,
  saveGame,
  SaveError,
  type SaveStorage,
} from '../saves/storage';
import { CombatScreen } from './screens/CombatScreen';
import type { CombatFeedback } from './components/CombatFeedback';
import { DeckDialog } from './components/DeckDialog';
import { SettingsDialog } from './components/SettingsDialog';
import { DefeatScreen, VictoryScreen } from './screens/EndScreen';
import { RewardScreen } from './screens/RewardScreen';
import { RouteScreen } from './screens/RouteScreen';
import { SystemScreen } from './screens/SystemScreen';
import { TitleScreen } from './screens/TitleScreen';
import { loadSettings, saveSettings, type GameSettings } from './settings';
import { loadProgression, recordVictory, type Progression } from './progression';

export type CommandHandler = (command: GameCommand) => boolean;

export interface AppProps {
  initialState?: GameState;
  storage?: SaveStorage;
}

interface LogEntry {
  id: number;
  text: string;
}

function cardName(state: GameState, cardId: string): string {
  const instance = state.run?.deck.find((card) => card.id === cardId);
  if (!instance) return 'a card';
  return resolveCardDefinition(instance, content).name;
}

function enemyName(state: GameState, enemyId: string): string {
  const enemy = state.combat?.enemies.find((candidate) => candidate.id === enemyId);
  return enemy ? (content.enemies[enemy.definitionId]?.name ?? 'Enemy') : 'Enemy';
}

function targetName(state: GameState, targetId: string): string {
  return targetId === 'player' ? 'The Mechanic' : enemyName(state, targetId);
}

function describeEvent(event: GameEvent, state: GameState): string {
  switch (event.type) {
    case 'run_started':
      return `Expedition ${event.seed} began.`;
    case 'combat_started':
      return `${content.encounters[event.encounterId]?.name ?? 'Combat'} began.`;
    case 'turn_started':
      return `Turn ${event.turn}. The forge is yours.`;
    case 'card_drawn':
      return `Drew ${cardName(state, event.cardId)}.`;
    case 'card_played':
      return `Played ${content.cards[event.definitionId]?.name ?? 'a card'}.`;
    case 'damage':
      return `${targetName(state, event.targetId)} took ${event.amount} damage${event.blocked > 0 ? ` (${event.blocked} blocked)` : ''}.`;
    case 'block_gained':
      return `${targetName(state, event.targetId)} gained ${event.amount} Block.`;
    case 'heat_changed':
      return `Heat shifted from ${event.from} to ${event.to}.`;
    case 'status_changed':
      return `${targetName(state, event.targetId)} gained ${event.amount} ${event.status}.`;
    case 'gear_worn':
      return `${cardName(state, event.cardId)} has ${event.durability} Durability left.`;
    case 'gear_repaired':
      return `${cardName(state, event.cardId)} was repaired to ${event.durability}.`;
    case 'enemy_defeated':
      return `${enemyName(state, event.enemyId)} fell.`;
    case 'enemy_phase_changed':
      return `${enemyName(state, event.enemyId)} entered phase ${event.phase}.`;
    case 'status_inserted':
      return `${content.cards[event.definitionId]?.name ?? 'A status card'} was forced into the deck.`;
    case 'overheated':
      return `Overheat dealt ${event.damage} unblocked damage.`;
    case 'combat_won':
      return `The route is clear. ${event.iron} Iron recovered.`;
    case 'combat_lost':
      return 'The line claimed another expedition.';
    case 'reward_chosen':
      if (event.cardDefinitionId) {
        return `${content.cards[event.cardDefinitionId]?.name ?? 'A card'} joined the deck.`;
      }
      if (event.relicId) return `${content.relics[event.relicId]?.name ?? 'A relic'} was recovered.`;
      return 'The salvage was left behind.';
    case 'route_chosen':
      return `${event.kind} route selected.`;
    case 'event_resolved':
      return `${content.events[event.eventId]?.title ?? 'The encounter'} was resolved.`;
    case 'forge_used':
      return `Forge action completed: ${event.action}.`;
    case 'iron_changed':
      return `Iron changed by ${event.amount}.`;
    case 'relic_gained':
      return `${content.relics[event.relicId]?.name ?? 'A relic'} was acquired.`;
    case 'card_added':
      return `${content.cards[event.definitionId]?.name ?? 'A card'} joined the deck.`;
    case 'card_removed':
      return `${cardName(state, event.cardId)} was removed from the deck.`;
    case 'card_upgraded':
      return `${cardName(state, event.cardId)} was upgraded.`;
  }
}

function makeLogEntries(
  events: GameEvent[],
  state: GameState,
  nextLogId: number,
): LogEntry[] {
  const drawn = events.filter((event) => event.type === 'card_drawn').length;
  let drawReported = false;
  return events.flatMap((event, index) => {
    if (event.type === 'card_drawn') {
      if (drawReported) return [];
      drawReported = true;
      return [{ id: nextLogId + index, text: `Drew ${drawn} ${drawn === 1 ? 'card' : 'cards'}.` }];
    }
    return [{ id: nextLogId + index, text: describeEvent(event, state) }];
  });
}

interface SaveLoadStatus {
  state?: GameState;
  error?: string;
}

function tryLoadSave(storage: SaveStorage | undefined): SaveLoadStatus {
  if (!storage) return {};
  try {
    return { state: loadGame(storage) };
  } catch (error) {
    return {
      error: error instanceof SaveError
        ? error.message
        : 'The saved expedition could not be read by this browser.',
    };
  }
}

function browserSaveStorage(): SaveStorage | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}

export function App({ initialState, storage }: AppProps) {
  const [saveStorage] = useState<SaveStorage | undefined>(() => storage ?? browserSaveStorage());
  const [game, setGame] = useState<GameState>(() => initialState ?? createInitialState());
  const [settings, setSettings] = useState<GameSettings>(() => loadSettings(saveStorage));
  const [progression, setProgression] = useState<Progression>(() => loadProgression(saveStorage));
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [deckOpen, setDeckOpen] = useState(false);
  const [saveStatus, setSaveStatus] = useState<SaveLoadStatus>(() =>
    initialState ? {} : tryLoadSave(saveStorage),
  );
  const [ruleError, setRuleError] = useState<string | null>(null);
  const [log, setLog] = useState<LogEntry[]>([]);
  const [feedback, setFeedback] = useState<CombatFeedback | null>(null);
  const [nextLogId, setNextLogId] = useState(1);
  const firstScreen = useRef(true);
  const audio = useRef<GameAudio | null>(null);
  if (!audio.current) audio.current = new GameAudio(settings);
  const savedRun = saveStatus.state;

  const send: CommandHandler = (command) => {
    try {
      const result = dispatch(game, command, content);
      audio.current?.play(result.events);
      const additions = makeLogEntries(result.events, result.state, nextLogId);
      const latestFirst = [...additions].reverse();
      const played = result.events.find((event) => event.type === 'card_played');
      setFeedback({
        id: nextLogId,
        events: result.events,
        title: played ? content.cards[played.definitionId]!.name : command.type === 'end_turn' ? 'Enemy turn resolved' : 'The encounter begins',
        summary: result.events
          .filter((event) => !['card_drawn', 'card_played', 'route_chosen', 'turn_started', 'combat_started'].includes(event.type))
          .map((event) => describeEvent(event, result.state))
          .join(' ') || 'Read the intents, spend Energy on cards, then end your turn.',
      });
      setGame(result.state);
      setLog((current) => [...latestFirst, ...current].slice(0, 30));
      setNextLogId((current) => current + result.events.length);
      if (saveStorage) {
        try {
          if (result.state.screen === 'title' || ['victory', 'defeat'].includes(result.state.screen)) {
            clearSave(saveStorage);
            setSaveStatus({});
          } else {
            saveGame(result.state, saveStorage);
            setSaveStatus({ state: result.state });
          }
          setRuleError(null);
        } catch (error) {
          setRuleError(
            error instanceof SaveError
              ? error.message
              : 'The expedition continued, but this browser could not save it.',
          );
        }
      } else {
        setRuleError(null);
      }
      if (result.state.screen === 'victory' && game.screen !== 'victory') {
        try {
          const nextProgression = recordVictory(progression, saveStorage);
          setProgression(nextProgression);
        } catch {
          setProgression((current) => ({ victories: current.victories + 1, deepeningUnlocked: true }));
          setRuleError('Victory is secure, but this browser could not remember the unlocked difficulty.');
        }
      }
      return true;
    } catch (error) {
      if (error instanceof GameRuleError) {
        setRuleError(error.message);
        return false;
      }
      setRuleError('The expedition could not continue because of an unexpected error.');
      return false;
    }
  };

  useEffect(
    () => exposeBrowserControl(
      () => game,
      send,
      (nextState) => {
        setGame(nextState);
        setLog([]);
        setFeedback(null);
        setRuleError(null);
      },
    ),
    [game, send],
  );

  useEffect(() => {
    if (firstScreen.current) {
      firstScreen.current = false;
      return;
    }
    const main = document.getElementById('main-content');
    if (!main) return;
    window.scrollTo(0, 0);
    main.tabIndex = -1;
    main.focus({ preventScroll: true });
  }, [game.screen, game.run?.node]);

  useEffect(() => {
    audio.current?.configure(settings);
    const root = document.documentElement;
    root.classList.toggle('ironvein-reduced-motion', settings.reducedMotion);
    root.classList.toggle('ironvein-large-text', settings.largeText);
    root.classList.toggle('ironvein-high-contrast', settings.highContrast);
    return () => {
      root.classList.remove('ironvein-reduced-motion', 'ironvein-large-text', 'ironvein-high-contrast');
    };
  }, [settings]);

  const changeSettings = (nextSettings: GameSettings) => {
    setSettings(nextSettings);
    try {
      saveSettings(nextSettings, saveStorage);
    } catch {
      setRuleError('Settings changed for this session, but this browser could not remember them.');
    }
  };

  let screen: ReactNode;
  switch (game.screen) {
    case 'title':
      screen = (
        <TitleScreen
          onCommand={send}
          savedRun={savedRun}
          saveError={saveStatus.error}
          deepeningUnlocked={progression.deepeningUnlocked}
          onContinue={() => {
            if (!savedRun) return;
            setGame(structuredClone(savedRun));
            setFeedback(null);
            setLog([]);
            setRuleError(null);
          }}
          onClearSave={() => {
            if (!saveStorage) return;
            try {
              clearSave(saveStorage);
              setSaveStatus({});
              setRuleError(null);
            } catch {
              setRuleError('This browser would not clear the unusable saved expedition.');
            }
          }}
        />
      );
      break;
    case 'route':
      screen = <RouteScreen state={game} onCommand={send} />;
      break;
    case 'combat':
      screen = <CombatScreen state={game} log={log} feedback={feedback} onCommand={send} />;
      break;
    case 'reward':
      screen = <RewardScreen state={game} onCommand={send} />;
      break;
    case 'event':
      screen = <SystemScreen state={game} system="event" onCommand={send} />;
      break;
    case 'trader':
      screen = <SystemScreen state={game} system="trader" onCommand={send} />;
      break;
    case 'forge':
      screen = <SystemScreen state={game} system="forge" onCommand={send} />;
      break;
    case 'defeat':
      screen = <DefeatScreen state={game} onCommand={send} />;
      break;
    case 'victory':
      screen = <VictoryScreen state={game} onCommand={send} />;
      break;
  }

  return (
    <div className={`app-shell app-shell--act-${game.run?.act ?? 0}`}>
      <a className="skip-link" href="#main-content">
        Skip to game
      </a>
      <button
        className="settings-toggle"
        type="button"
        aria-label="Settings"
        aria-haspopup="dialog"
        aria-expanded={settingsOpen}
        onClick={() => {
          setDeckOpen(false);
          setSettingsOpen(true);
        }}
      >
        <span aria-hidden="true">◇</span> <span className="settings-toggle__label">Settings</span>
      </button>
      {game.run ? (
        <button
          className="deck-toggle"
          type="button"
          aria-label="Inspect deck and card piles"
          aria-haspopup="dialog"
          aria-expanded={deckOpen}
          onClick={() => {
            setSettingsOpen(false);
            setDeckOpen(true);
          }}
        >
          <span aria-hidden="true">▤</span> <span className="deck-toggle__label">Deck</span>
        </button>
      ) : null}
      <div className="screen-reader-status" aria-live="polite" aria-atomic="true">
        {game.screen === 'title' ? 'Cathedris title screen' : `${game.screen} screen`}
      </div>
      {ruleError ? (
        <div className="rule-error" role="alert">
          <span>
            <strong>Action blocked.</strong> {ruleError}
          </span>
          <button type="button" aria-label="Dismiss error" onClick={() => setRuleError(null)}>
            Dismiss
          </button>
        </div>
      ) : null}
      {screen}
      <DeckDialog open={deckOpen} state={game} onClose={() => setDeckOpen(false)} />
      <SettingsDialog
        open={settingsOpen}
        settings={settings}
        hasActiveRun={Boolean(game.run && !['title', 'victory', 'defeat'].includes(game.screen))}
        onChange={changeSettings}
        onClose={() => setSettingsOpen(false)}
        onAbandon={() => {
          if (send({ type: 'abandon_run' })) setSettingsOpen(false);
        }}
      />
    </div>
  );
}
